<div class="geo-filter" [class]="'geo-filter--' + fit" [style.width]="width" [class.geo-filter--disabled]="isDisabled()">
  @if (!hiddenlabel) {
    <label class="geo-filter__label" [attr.for]="inputId">
      {{ label }}
      @if (required) {
        <span class="geo-filter__required" aria-hidden="true">*</span>
      }
    </label>
  }

  @if (loading()) {
    <div class="geo-filter__status">
      <mc-loading-indicator fit="small" orientation="horizontal" label="Loading geography"></mc-loading-indicator>
    </div>
  } @else if (loadFailed()) {
    <!-- The control is unusable without the tree, so offer a retry rather than a disabled empty select. -->
    <mc-notification appearance="error" fit="small" icon="exclamation-triangle" verticalalign="middle">
      <span>Geography could not be loaded.</span>
      <span slot="actions">
        <!-- preventDefault cancels the shadow button's own Enter-to-click, so retry() runs once. -->
        <mc-button
          fit="small"
          variant="plain"
          appearance="error"
          icon="arrow-clockwise"
          label="Retry"
          (click)="retry()"
          (keydown.enter)="$event.preventDefault(); retry()"></mc-button>
      </span>
    </mc-notification>
  } @else if (noAccess()) {
    <mc-notification appearance="info" fit="small" icon="info-circle" verticalalign="middle">
      <span>You don't have access to any shops yet. Ask an administrator to assign a region or shop.</span>
    </mc-notification>
  } @else if (singleShop(); as shop) {
    <!-- The median user has exactly one shop — a fixed, preselected label beats a picker with one option. -->
    <div class="geo-filter__fixed">
      <mc-tag appearance="info" fit="small" class="geo-filter__chip" data-level="shop">Shop · {{ shop.code }} {{ shop.name }}</mc-tag>
    </div>
  } @else {
    <div class="geo-filter__line">
      <!-- The field and its popup share this wrapper so the popup stays the width of the field
           rather than the width of the row the shortcut sits on. -->
      <div class="geo-filter__combo">
        <!-- Click-to-focus and row selection are delegated to host listeners in the component, so the
             pointer handling lives in one place and the rows stay non-focusable. -->
        <div class="geo-filter__field" [class.is-focused]="focused()" [class.is-invalid]="showError()" [class.is-open]="showPopup()">
          <mc-icon class="geo-filter__lead-icon" icon="magnifying-glass" size="20"></mc-icon>

          <div class="geo-filter__entry">
            @for (chip of chips(); track chip.key) {
              <!-- Level, code and name — never a bare code: "NEU" alone is unreadable, and once shop
               codes collide with region codes it becomes actively wrong. -->
              <mc-tag
                class="geo-filter__chip"
                [class.is-covered]="chip.coveredBy"
                [class.is-flashing]="flashedKey() === chip.key"
                [attr.appearance]="chip.coveredBy ? 'neutral-weak' : 'info'"
                fit="small"
                withaction
                [attr.data-level]="chip.level"
                [attr.title]="
                  chip.coveredBy
                    ? chip.levelLabel + ' ' + chip.code + ' — already covered by ' + chip.coveredByName
                    : chip.levelLabel + ' ' + chip.code + ' ' + chip.name
                "
                (dismiss)="removeKey(chip.key)"
                (keydown)="onChipKeydown($event, chip.key)">
                {{ chip.levelLabel }} · {{ chip.code }} {{ chip.name }}
              </mc-tag>
            }

            <input
              #queryInput
              class="geo-filter__input"
              type="text"
              role="combobox"
              autocomplete="off"
              autocapitalize="off"
              spellcheck="false"
              aria-autocomplete="list"
              aria-haspopup="listbox"
              [id]="inputId"
              [attr.name]="inputId"
              [attr.aria-label]="hiddenlabel ? label : null"
              [attr.placeholder]="chips().length > 0 ? '' : placeholder"
              [attr.aria-expanded]="showPopup()"
              [attr.aria-controls]="listboxId"
              [attr.aria-activedescendant]="activeOptionId() || null"
              [attr.aria-describedby]="noticeId"
              [attr.aria-invalid]="showError()"
              [attr.aria-required]="required"
              [disabled]="isDisabled()"
              (input)="onQueryInput($event)"
              (focus)="onFocus()"
              (keydown)="onKeydown($event)" />
          </div>

          @if (chips().length > 0 && !isDisabled()) {
            <mc-button
              class="geo-filter__clear"
              fit="small"
              padding="compact"
              variant="plain"
              appearance="neutral"
              icon="times"
              hiddenlabel
              [attr.label]="'Clear ' + label"
              (click)="clearAll()"
              (keydown.enter)="$event.preventDefault(); clearAll()"></mc-button>
          }
        </div>

        @if (showPopup()) {
          <div class="geo-filter__popup">
            @if (sections().length === 0) {
              <p class="geo-filter__no-match">No region, cluster, country, location or shop matches “{{ query() }}”.</p>
            } @else {
              <!-- Div-based listbox per the WAI-ARIA combobox pattern: rows are never focusable, the
                   input keeps focus and points at the active row with aria-activedescendant. -->
              <div
                class="geo-filter__list"
                role="listbox"
                [id]="listboxId"
                [attr.aria-label]="label + ' results'"
                aria-multiselectable="true">
                @for (section of sections(); track section.level) {
                  <!-- Fixed broadest → narrowest order, never re-ranked by score, empty sections
                       omitted. The group's name carries the true match count and any truncation, so
                       the visual header and the "N more" line are hidden from assistive tech. -->
                  <div class="geo-filter__group" role="group" [attr.aria-label]="section.ariaLabel" [attr.data-level]="section.level">
                    <div class="geo-filter__section" aria-hidden="true">
                      <span class="geo-filter__section-name">{{ section.label }}</span>
                      <span class="geo-filter__section-count">{{ section.matchCount }}</span>
                    </div>

                    @for (row of section.rows; track row.key) {
                      <div
                        class="geo-filter__row"
                        role="option"
                        [id]="optionId(row.rowIndex)"
                        [attr.data-option-index]="row.rowIndex"
                        [attr.data-level]="row.level"
                        [attr.aria-selected]="row.selected"
                        [class.is-active]="activeIndex() === row.rowIndex"
                        [class.is-selected]="row.selected"
                        [class.is-covered]="row.covered"
                        [class.is-shop]="row.level === 'shop'">
                        <span class="geo-filter__row-code">{{ row.code }}</span>
                        <span class="geo-filter__row-name">{{ row.name }}</span>
                        @if (row.shopsLabel) {
                          <span class="geo-filter__row-count">{{ row.shopsLabel }}</span>
                        }
                        <!-- Shop and location names repeat heavily, so the ancestor path is what makes
                             a row unambiguous. Required on every result, empty on regions. -->
                        <span class="geo-filter__row-path">
                          {{ row.path }}
                          @if (row.covered) {
                            <span class="geo-filter__row-flag">already covered by {{ row.coveredByName }}</span>
                          } @else if (row.selected) {
                            <span class="geo-filter__row-flag">selected</span>
                          }
                        </span>
                      </div>
                    }

                    @if (section.hiddenCount > 0) {
                      <!-- Each section truncates on its own, so 900 location matches can't bury two shops. -->
                      <div class="geo-filter__more" aria-hidden="true">
                        {{ section.hiddenCount }} more {{ section.hiddenLabel }} — keep typing to narrow
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>

      @if (isUnrestricted()) {
        <mc-button
          class="geo-filter__all-shops"
          fit="small"
          variant="outlined"
          appearance="neutral"
          icon="globe"
          label="All shops"
          [disabled]="isDisabled()"
          (click)="selectAllShops()"
          (keydown.enter)="$event.preventDefault(); selectAllShops()"></mc-button>
      }
    </div>

    <!-- Redundancy is information, not a validation failure, so it lives in a polite live region.
         The output element carries an implicit status role, so no explicit role is needed here. -->
    <output class="geo-filter__notice" [id]="noticeId" aria-live="polite">
      @if (notice(); as message) {
        <mc-notification
          fit="small"
          verticalalign="middle"
          closable
          [attr.appearance]="message.appearance"
          [attr.icon]="message.icon"
          (close)="dismissNotice()">
          <span>{{ message.message }}</span>
          @if (message.action; as action) {
            <span slot="actions">
              <mc-button
                fit="small"
                variant="plain"
                appearance="primary"
                [attr.label]="action.label"
                [disabled]="isDisabled()"
                (click)="runNoticeAction()"
                (keydown.enter)="$event.preventDefault(); runNoticeAction()"></mc-button>
            </span>
          }
        </mc-notification>
      }
    </output>

    @if (showError()) {
      <p class="geo-filter__error">{{ errorText }}</p>
    } @else if (hint) {
      <p class="geo-filter__hint">{{ hint }}</p>
    }
  }
</div>
